// index.tsx
// Paginator component for navigating paginated data tables.
// Provides a styled wrapper around the Forge Paginator and displays the current item range.

import { Paginator as ForgePaginator } from "@/vendor/Forge/dist";
import { styled } from "@stitches/react";

/**
 * Wrapper for the paginator controls and range display.
 */
const PaginatorWrapper = styled("div", {
  display: "flex",
  alignItems: "center",
  marginRight: 12,

  "& > div:first-child": {
    color: "#7C7D7D",
  },
});

/**
 * Helper text for displaying the current range of items.
 */
const HelperText = styled("div", {
  fontSize: 16,
  lineHeight: "20px",
});

/**
 * Default number of rows per page.
 */
export const ROWS_PER_PAGE = 20;

/**
 * Props for the Paginator component
 * @property currentPage - The current page (1-based)
 * @property totalItems - Total number of items in the dataset
 * @property rowsPerPage - Number of rows per page (optional)
 * @property totalPages - Total number of pages (optional)
 * @property onPaginationChange - Callback when the page changes
 */
interface Props {
  currentPage: number;
  totalItems: number;
  rowsPerPage?: number;
  totalPages?: number;
  onPaginationChange: (pageCount: number) => void;
}

/**
 * Calculates the display range of items for the current page.
 * @param rowsPerPage - Number of rows per page
 * @param currentPage - The current page (1-based)
 * @param totalItems - Total number of items
 * @returns A string representing the range, e.g. "1-20"
 */
export const getRange = ({
  rowsPerPage = ROWS_PER_PAGE,
  currentPage,
  totalItems,
}: Omit<Props, "onPaginationChange">) => {
  const lowerRange = rowsPerPage * (currentPage - 1) + 1;
  const upperRange = rowsPerPage * currentPage;
  const trueUpperRange = upperRange > totalItems ? totalItems : upperRange;

  return `${lowerRange}-${trueUpperRange}`;
};

/**
 * Paginator Component
 * Renders pagination controls and the current item range for a data table.
 * @param currentPage - The current page (1-based)
 * @param totalItems - Total number of items
 * @param rowsPerPage - Number of rows per page (optional)
 * @param totalPages - Total number of pages (optional)
 * @param onPaginationChange - Callback when the page changes
 */
const Paginator = ({
  currentPage, // 1-based pagination
  totalItems,
  rowsPerPage = ROWS_PER_PAGE,
  totalPages,
  onPaginationChange,
}: Props) => {
  if (totalItems === 0) return null;

  return (
    <PaginatorWrapper data-testid="paginator-wrapper">
      <HelperText>
        {getRange({ currentPage, totalItems, rowsPerPage })} of {totalItems}
      </HelperText>
      <ForgePaginator
        pageCount={totalPages}
        onSelectedIndexChange={onPaginationChange}
        selectedIndex={currentPage - 1}
      />
    </PaginatorWrapper>
  );
};

export default Paginator;
